/**
 * Held referral code (TMMAPP-0015, MM-D-85). A referral code is public marketing data, not a
 * credential — the in-memory-only rule covers access tokens. Stored under `tmm_ref` as
 * `{ code, seenAt }`; expired after 30 days from `seenAt` on read.
 */
const STORAGE_KEY = 'tmm_ref';
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
const CODE_PATTERN = /^[A-Za-z0-9_-]{3,32}$/;

interface StoredReferral {
  code: string;
  seenAt: number;
}

function readStored(): StoredReferral | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return null;
    const row = parsed as Record<string, unknown>;
    if (typeof row.code !== 'string' || typeof row.seenAt !== 'number') return null;
    return { code: row.code, seenAt: row.seenAt };
  } catch {
    return null;
  }
}

export function rememberReferral(code: string | null | undefined): void {
  if (typeof code !== 'string') return;
  const trimmed = code.trim();
  if (!CODE_PATTERN.test(trimmed)) return;
  const payload: StoredReferral = { code: trimmed, seenAt: Date.now() };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Quota / private mode — the code is simply not held.
  }
}

export function peekReferral(): string | null {
  const stored = readStored();
  if (!stored) return null;
  if (Date.now() - stored.seenAt > MAX_AGE_MS) {
    clearReferral();
    return null;
  }
  if (!CODE_PATTERN.test(stored.code)) {
    clearReferral();
    return null;
  }
  return stored.code;
}

export function clearReferral(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
